import React from 'react';
import MetricCard, { MetricCardGroup } from './MetricCard';
import Panel from './Panel';
import DonutBreakdown from './charts/DonutBreakdown';
import StackedTrendChart from './charts/StackedTrendChart';
import SimpleTrendChart from './charts/SimpleTrendChart';
import SimpleBarChart from './charts/SimpleBarChart';
import DataTable from './DataTable';
import MttrDetailedTable from './MttrDetailedTable';
import { qualityOverview as data } from './mockData';
import { ORIGIN_SERIES, DSI_TIER_SERIES, DSI_LINE } from './chartColors';
import useServiceDisruptionSummary from '../../hooks/useServiceDisruptionSummary';
import useServiceDisruptionOpen from '../../hooks/useServiceDisruptionOpen';
import useSupportBugsOpen from '../../hooks/useSupportBugsOpen';
import useServiceDisruptionBreakdown from '../../hooks/useServiceDisruptionBreakdown';
import useMeanTimeToResolveSummary from '../../hooks/useMeanTimeToResolveSummary';
import useMeanTimeToResolveBreakdown from '../../hooks/useMeanTimeToResolveBreakdown';
import useRequirementDefectRatio from '../../hooks/useRequirementDefectRatio';
import useRequirementDefectRatioBreakdown from '../../hooks/useRequirementDefectRatioBreakdown';
import useDefectLeakageRatioSummary from '../../hooks/useDefectLeakageRatioSummary';
import useDefectLeakageRatioBreakdown from '../../hooks/useDefectLeakageRatioBreakdown';
import useDefectSeverityIndex from '../../hooks/useDefectSeverityIndex';
import useDefectSeverityIndexBreakdown from '../../hooks/useDefectSeverityIndexBreakdown';
import { formatMttr } from '../../utils/formatMttr';
import { JIRA_BASE } from './constants';

// The API nests tier counts under tierCounts and returns an unrounded dsi — same flattening as
// DefectSeverityIndexTab's own flattenDsiRows (no shared util between the two tabs).
const flattenDsiRows = (rows) => (rows ?? []).map(({ tierCounts, dsi, ...rest }) => ({
  ...rest,
  ...tierCounts,
  dsi: Math.round(dsi * 100) / 100,
}));

// Business threshold from the ticket, not returned by the API.
const DLR_TARGET_PERCENT = 20;

// Transform origin-week/origin-month flat data into pivoted format for StackedTrendChart
// Normalizes origin keys to lowercase to match ORIGIN_SERIES configuration (cs, hj, heap)
const pivotOriginData = (flatData) => {
  if (!flatData || !Array.isArray(flatData)) return [];
  const pivoted = {};
  flatData.forEach(row => {
    if (!pivoted[row.bucket]) {
      pivoted[row.bucket] = { bucket: row.bucket };
    }
    // Normalize origin to lowercase to match ORIGIN_SERIES keys (cs, hj, heap)
    const originKey = (row.origin || 'unlabeled').toLowerCase();
    pivoted[row.bucket][originKey] = row.count || 0;
  });
  return Object.values(pivoted).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
};

const OPEN_SD_COLUMNS = [
  { key: 'domain', label: 'Product Domain' },
  { key: 'group', label: 'Product Group' },
  { key: 'unit', label: 'Product Unit' },
  { key: 'summary', label: 'Bug Summary', render: r => <a href={`${JIRA_BASE}/${r.key}`} target="_blank" rel="noopener noreferrer">{r.summary}</a> },
  { key: 'reportedOn', label: 'Reported On' },
  { key: 'status', label: 'Status' },
  { key: 'identifiedBy', label: 'Identified By' },
  { key: 'labels', label: 'Labels' },
  { key: 'rootCause', label: 'Rootcause' },
];

const OPEN_SUPPORT_COLUMNS = [
  { key: 'domain', label: 'Product Domain' },
  { key: 'group', label: 'Product Group' },
  { key: 'unit', label: 'Product Unit' },
  { key: 'summary', label: 'Bug Summary', render: r => <a href={`${JIRA_BASE}/${r.key}`} target="_blank" rel="noopener noreferrer">{r.summary}</a> },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
];

function QualityOverviewTab({ filters, dateRange }) {
  const m = data.topMetrics;
  const { data: serviceDisruptionSummary, loading: sdLoading, error: sdError } = useServiceDisruptionSummary(filters, dateRange);
  const { data: serviceDisruptionOpenData, loading: sdOpenLoading, error: sdOpenError } = useServiceDisruptionOpen(filters, dateRange);
  const { data: supportBugsData, loading: sbLoading, error: sbError } = useSupportBugsOpen(filters, dateRange);
  const { data: identifiedByData, loading: idByLoading, error: idByError } = useServiceDisruptionBreakdown('identified-by', filters, dateRange);
  const { data: rootcauseData, loading: rcLoading, error: rcError } = useServiceDisruptionBreakdown('rootcause', filters, dateRange);
  const { data: originWeekData, loading: owLoading, error: owError } = useServiceDisruptionBreakdown('origin-week', filters, dateRange);
  const { data: originMonthData, loading: omLoading, error: omError } = useServiceDisruptionBreakdown('origin-month', filters, dateRange);
  const { data: unitData, loading: unitLoading, error: unitError } = useServiceDisruptionBreakdown('unit', filters, dateRange);
  const { data: mttrSummary, loading: mttrLoading, error: mttrError } = useMeanTimeToResolveSummary(filters, dateRange);
  const { data: mttrByUnit, loading: mttrByUnitLoading, error: mttrByUnitError } = useMeanTimeToResolveBreakdown('unit', filters, dateRange);
  const { data: mttrByWeek, loading: mttrByWeekLoading, error: mttrByWeekError } = useMeanTimeToResolveBreakdown('week', filters, dateRange);
  const { data: mttrByMonth, loading: mttrByMonthLoading, error: mttrByMonthError } = useMeanTimeToResolveBreakdown('month', filters, dateRange);
  const { data: requirementDefectRatioData, loading: rdrLoading, error: rdrError } = useRequirementDefectRatio(filters, dateRange);
  const { data: rdrBreakdownData, loading: rdrBreakdownLoading, error: rdrBreakdownError } = useRequirementDefectRatioBreakdown('unit', filters, dateRange);
  const { data: dlrSummary, loading: dlrLoading, error: dlrError } = useDefectLeakageRatioSummary(filters, dateRange);
  const { data: dlrByWeek, loading: dlrByWeekLoading, error: dlrByWeekError } = useDefectLeakageRatioBreakdown('week', filters, dateRange);
  const { data: dlrByUnit, loading: dlrByUnitLoading, error: dlrByUnitError } = useDefectLeakageRatioBreakdown('unit', filters, dateRange);
  const { data: dsiSummary, loading: dsiLoading, error: dsiError } = useDefectSeverityIndex(filters, dateRange);
  const { data: dsiByWeek, loading: dsiByWeekLoading, error: dsiByWeekError } = useDefectSeverityIndexBreakdown('week', filters, dateRange);
  const { data: dsiByUnit, loading: dsiByUnitLoading, error: dsiByUnitError } = useDefectSeverityIndexBreakdown('unit', filters, dateRange);
  const sdSummaryData = serviceDisruptionSummary?.serviceDisruption;
  const sdCommData = serviceDisruptionSummary?.communicationToClients;
  const sdValue = sdLoading ? 'Loading...' : sdSummaryData?.count ?? 'Unavailable';
  const communicationValue = sdLoading ? 'Loading...' : sdCommData?.count ?? 'Unavailable';
  const sdChange = sdSummaryData?.previousCount > 0
    ? Math.round(((sdSummaryData.count - sdSummaryData.previousCount) / sdSummaryData.previousCount) * 1000) / 10
    : null;
  const mttrHoursRaw = mttrLoading ? null : mttrSummary?.mttrHours;
  const mttrDisplay = mttrLoading ? { value: 'Loading...', suffix: '' } : formatMttr(mttrHoursRaw);
  const mttrValue = mttrDisplay.value;
  const mttrSuffix = mttrDisplay.suffix;
  const mttrChange = mttrSummary?.mttrHours > 0 && mttrSummary?.previousMttrHours > 0
    ? Math.round(((mttrSummary.mttrHours - mttrSummary.previousMttrHours) / mttrSummary.previousMttrHours) * 1000) / 10
    : null;
  // API returns ratio as a 0..1 fraction (e.g. 0.0051 = 0.51%), so *1000/10 both converts to a
  // percentage and rounds to 1 decimal in one step, same convention as sdChange/mttrChange above.
  // ratio is null (not 0) when the period has no Support/EPD defects at all — shown distinctly
  // from a real 0% ("no leakage, everything healthy") rather than defaulting to it.
  const dlrValue = dlrLoading ? 'Loading...'
    : !dlrSummary ? 'Unavailable'
    : dlrSummary.ratio === null ? 'No data'
    : Math.round(dlrSummary.ratio * 1000) / 10;
  // Both sides must be real numbers: previousRatio > 0 already excludes null (null > 0 is false
  // in JS) and 0 itself, but ratio alone being null would still slip through and compute a
  // misleading change against a stale `null - previousRatio` subtraction if not checked too.
  const dlrChange = typeof dlrSummary?.ratio === 'number' && dlrSummary?.previousRatio > 0
    ? Math.round(((dlrSummary.ratio - dlrSummary.previousRatio) / dlrSummary.previousRatio) * 1000) / 10
    : null;
  // No previous-period figure in the API response — same precedent as DefectSeverityIndexTab's
  // own DSI card, which also renders without a change indicator.
  const dsiValue = dsiLoading ? 'Loading...' : !dsiSummary ? 'Unavailable' : Math.round(dsiSummary.dsi * 100) / 100;

  // Same 0..1-fraction-to-percent conversion as dlrValue above, applied per bucket. A bucket's
  // own ratio is null (not 0) when that bucket had no Support/EPD defects at all — left as null
  // here too, rather than coerced to 0, so the chart can show a gap instead of a false 0%.
  const toDlrPercentBuckets = (buckets) => (buckets ?? []).map(b => ({
    ...b,
    ratio: b.ratio === null ? null : Math.round(b.ratio * 1000) / 10
  }));
  const dlrByWeekChart = toDlrPercentBuckets(dlrByWeek);
  const dlrByUnitChart = toDlrPercentBuckets(dlrByUnit);

  // Map real data from API
  const serviceDisruptionRows = serviceDisruptionOpenData?.data ?? [];
  const supportBugsRows = supportBugsData?.data ?? [];

  return (
    <div className="qk-tab-content">
      {sdError && <div role="alert" className="section-error">Couldn't load Service Disruption summary.</div>}
      <Panel accent="purple">
        <div className="qk-overview-grid">
          <MetricCardGroup title="Product Stability & Reliability">
            <MetricCard label="#Service Disruption" value={sdValue}
              change={sdChange} changeLabel="from previous period" />
            <MetricCard label="Defect Severity Index (DSI)" value={dsiValue}
              goodDirection="down" info="See Defect Severity Index tab" />
            <MetricCard label="Mean Time to Resolve (MTTR)" value={mttrValue} suffix={mttrSuffix}
              change={mttrChange} changeLabel="from previous period" />
            <MetricCard label="Change Failure Rate (CFR)" tbd />
          </MetricCardGroup>
          <MetricCardGroup title="Testing Effectiveness">
            <MetricCard label="Requirement Defect Ratio (RDR)" value={rdrLoading ? 'Loading...' : requirementDefectRatioData?.requirementDefectRatio?.ratio ? requirementDefectRatioData.requirementDefectRatio.ratio.toFixed(3) : 'Unavailable'} suffix="%"
              change={requirementDefectRatioData?.requirementDefectRatio && requirementDefectRatioData.requirementDefectRatio.previousRatio > 0 ? Math.round(((requirementDefectRatioData.requirementDefectRatio.ratio - requirementDefectRatioData.requirementDefectRatio.previousRatio) / requirementDefectRatioData.requirementDefectRatio.previousRatio) * 1000) / 10 : null} info="Formula unresolved — see QA-7145" />
            <MetricCard label="Functional Automation Coverage" tbd />
            <MetricCard label="Defect Leakage Ratio (DLR)" value={dlrValue} suffix="%" target={`<${DLR_TARGET_PERCENT}%`}
              change={dlrChange} />
            <MetricCard label="Rate of Regression (RoR)" value={m.ror.ratio} suffix="%"
              change={Math.round(((m.ror.ratio - m.ror.previousRatio) / m.ror.previousRatio) * 1000) / 10} goodDirection="down" />
          </MetricCardGroup>
        </div>
      </Panel>

      {/* Service Disruption */}
      <Panel accent="purple">
        {(idByError || rcError || owError || omError || unitError) && (
          <div role="alert" className="section-error">Couldn't load service disruption breakdowns.</div>
        )}
        <MetricCardGroup>
          <MetricCard label="#Service Disruption" value={sdValue} />
          <MetricCard label="#Communication to Clients" value={communicationValue} />
        </MetricCardGroup>
        <div className="qk-chart-row">
          <div className="qk-chart-col">
            <div className="qk-chart-title">Service Disruption Identified by</div>
            {idByLoading && <div className="section-loading">Loading…</div>}
            {!idByLoading && identifiedByData?.data && <DonutBreakdown data={identifiedByData.data} />}
            {!idByLoading && !identifiedByData?.data && <div className="qk-chart-empty">No data available</div>}
          </div>
          <div className="qk-chart-col">
            <div className="qk-chart-title">Service Disruption by Rootcause</div>
            {rcLoading && <div className="section-loading">Loading…</div>}
            {!rcLoading && rootcauseData?.data && <DonutBreakdown data={rootcauseData.data} />}
            {!rcLoading && !rootcauseData?.data && <div className="qk-chart-empty">No data available</div>}
          </div>
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">By Origin CS/HJ/Heap — Week over Week</div>
            {owLoading && <div className="section-loading">Loading…</div>}
            {!owLoading && originWeekData?.data && <StackedTrendChart data={pivotOriginData(originWeekData.data)} dimensionKey="bucket" series={ORIGIN_SERIES} height={180} />}
            {!owLoading && !originWeekData?.data && <div className="qk-chart-empty">No data available</div>}
          </div>
        </div>
        <div className="qk-chart-row">
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">Service Disruption per Domain/Unit (top 15 units)</div>
            {unitLoading && <div className="section-loading">Loading…</div>}
            {!unitLoading && unitData?.data && <SimpleBarChart data={unitData.data} dimensionKey="bucket" valueKey="count" color="#7c3aed" />}
            {!unitLoading && !unitData?.data && <div className="qk-chart-empty">No data available</div>}
            {!unitLoading && unitData?.note && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '8px' }}>{unitData.note}</div>}
          </div>
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">By Origin CS/HJ/Heap — Month over Month</div>
            {omLoading && <div className="section-loading">Loading…</div>}
            {!omLoading && originMonthData?.data && <StackedTrendChart data={pivotOriginData(originMonthData.data)} dimensionKey="bucket" series={ORIGIN_SERIES} height={180} />}
            {!omLoading && !originMonthData?.data && <div className="qk-chart-empty">No data available</div>}
          </div>
        </div>
      </Panel>

      {/* MTTR */}
      <Panel accent="purple">
        {(mttrError || mttrByUnitError || mttrByWeekError || mttrByMonthError) && (
          <div role="alert" className="section-error">Couldn't load Mean Time to Resolve data.</div>
        )}
        <MetricCardGroup>
          <MetricCard label="Mean Time to Resolve [MTTR]" value={mttrValue} suffix={mttrSuffix}
            change={mttrChange} changeLabel="from previous period" />
        </MetricCardGroup>
        <div className="qk-chart-row">
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">[MTTR] Mean Time to Resolve — Per Product Unit</div>
            {mttrByUnitLoading && <div className="section-loading">Loading…</div>}
            {!mttrByUnitLoading && mttrByUnit?.length > 0 && <SimpleBarChart data={mttrByUnit} dimensionKey="bucket" valueKey="mttrHours" color="#c2185b" valueSuffix=" hrs" />}
            {!mttrByUnitLoading && !mttrByUnitError && (!mttrByUnit || mttrByUnit.length === 0) && <div className="qk-chart-empty">No data available</div>}
          </div>
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">[MTTR] Mean Time to Resolve — Week Over Week</div>
            {mttrByWeekLoading && <div className="section-loading">Loading…</div>}
            {!mttrByWeekLoading && mttrByWeek?.length > 0 && <SimpleTrendChart data={mttrByWeek} dimensionKey="bucket" valueKey="mttrHours" color="#16a34a" valueSuffix=" hrs" />}
            {!mttrByWeekLoading && !mttrByWeekError && (!mttrByWeek || mttrByWeek.length === 0) && <div className="qk-chart-empty">No data available</div>}
          </div>
        </div>
        <div className="qk-chart-row">
          <div className="qk-chart-col qk-chart-col--wide">
            <div className="qk-chart-title">[MTTR] Mean Time to Resolve — Month Over Month</div>
            {mttrByMonthLoading && <div className="section-loading">Loading…</div>}
            {!mttrByMonthLoading && mttrByMonth?.length > 0 && <SimpleTrendChart data={mttrByMonth} dimensionKey="bucket" valueKey="mttrHours" color="#16a34a" valueSuffix=" hrs" />}
            {!mttrByMonthLoading && !mttrByMonthError && (!mttrByMonth || mttrByMonth.length === 0) && <div className="qk-chart-empty">No data available</div>}
          </div>
        </div>
        <MttrDetailedTable filters={filters} dateRange={dateRange} />
      </Panel>

      {/* DSI */}
      <Panel accent="green">
        {(dsiError || dsiByWeekError || dsiByUnitError) && (
          <div role="alert" className="section-error">Couldn't load Defect Severity Index data.</div>
        )}
        <MetricCardGroup>
          <MetricCard label="Defect Severity Index (DSI)" value={dsiValue} info="See Defect Severity Index tab for the full breakdown" />
        </MetricCardGroup>
        <div className="qk-chart-title">Defect Severity Index - Severity Showdown: Weekly Battle</div>
        {dsiByWeekLoading && <div className="section-loading">Loading…</div>}
        {!dsiByWeekLoading && dsiByWeek?.length > 0 && <StackedTrendChart data={flattenDsiRows(dsiByWeek)} dimensionKey="bucket" series={DSI_TIER_SERIES} line={DSI_LINE} />}
        {!dsiByWeekLoading && !dsiByWeekError && (!dsiByWeek || dsiByWeek.length === 0) && <div className="qk-chart-empty">No data available</div>}
        <div className="qk-chart-title">Defect Severity Index - Per Team</div>
        {dsiByUnitLoading && <div className="section-loading">Loading…</div>}
        {!dsiByUnitLoading && dsiByUnit?.length > 0 && <StackedTrendChart data={flattenDsiRows(dsiByUnit)} dimensionKey="bucket" series={DSI_TIER_SERIES} line={DSI_LINE} />}
        {!dsiByUnitLoading && !dsiByUnitError && (!dsiByUnit || dsiByUnit.length === 0) && <div className="qk-chart-empty">No data available</div>}
      </Panel>

      {/* Defect Leakage Ratio */}
      <Panel accent="pink">
        {(dlrError || dlrByWeekError || dlrByUnitError) && (
          <div className="section-error">Couldn't load Defect Leakage Ratio.</div>
        )}
        <MetricCardGroup>
          <MetricCard label="Defect Leakage Ratio [DLR]" value={dlrValue} suffix="%" target={`<${DLR_TARGET_PERCENT}%`}
            change={dlrChange} />
        </MetricCardGroup>
        <div className="qk-chart-title">Defect Leakage — Weekly Leak Watch</div>
        {dlrByWeekLoading && <div className="section-loading">Loading…</div>}
        {!dlrByWeekLoading && dlrByWeekChart.length > 0 && (
          <SimpleTrendChart data={dlrByWeekChart} dimensionKey="bucket" valueKey="ratio" color="#c2185b" valueSuffix="%" />
        )}
        {!dlrByWeekLoading && !dlrByWeekError && dlrByWeekChart.length === 0 && (
          <div className="qk-chart-empty">No data available</div>
        )}
        <div className="qk-chart-title">Defect Leakage — Leak Delay Shield</div>
        {dlrByUnitLoading && <div className="section-loading">Loading…</div>}
        {!dlrByUnitLoading && dlrByUnitChart.length > 0 && (
          <SimpleBarChart data={dlrByUnitChart} dimensionKey="bucket" valueKey="ratio" color="#c2185b" valueSuffix="%" />
        )}
        {!dlrByUnitLoading && !dlrByUnitError && dlrByUnitChart.length === 0 && (
          <div className="qk-chart-empty">No data available</div>
        )}
      </Panel>

      {/* Requirement Defect Ratio */}
      <Panel accent="blue">
        {rdrError && <div className="section-error">Couldn't load Requirement Defect Ratio.</div>}
        <MetricCardGroup>
          <MetricCard label="Requirement Defect Ratio [RDR]" value={rdrLoading ? 'Loading...' : requirementDefectRatioData?.requirementDefectRatio?.ratio ? requirementDefectRatioData.requirementDefectRatio.ratio.toFixed(3) : 'Unavailable'} suffix="%"
            change={requirementDefectRatioData?.requirementDefectRatio && requirementDefectRatioData.requirementDefectRatio.previousRatio > 0 ? Math.round(((requirementDefectRatioData.requirementDefectRatio.ratio - requirementDefectRatioData.requirementDefectRatio.previousRatio) / requirementDefectRatioData.requirementDefectRatio.previousRatio) * 1000) / 10 : null}
            info="Formula unresolved upstream — see QA-7145" />
        </MetricCardGroup>
        <div className="qk-chart-title">Requirement Defect - Per Team</div>
        {rdrBreakdownError && <div className="section-error">Couldn't load Requirement Defect breakdown.</div>}
        {rdrBreakdownLoading && <div className="section-loading">Loading…</div>}
        {!rdrBreakdownLoading && !rdrBreakdownError && rdrBreakdownData?.data && <SimpleBarChart data={rdrBreakdownData.data} dimensionKey="bucket" valueKey="ratio" color="#2563eb" valueSuffix="%" />}
        {!rdrBreakdownLoading && !rdrBreakdownError && !rdrBreakdownData?.data && <div className="qk-chart-empty">No data available</div>}
      </Panel>

      {/* Detail tables */}
      <Panel title="Open Service Disruption" accent="pink">
        {sdOpenError && <div className="section-error">Couldn't load service disruptions.</div>}
        {sdOpenLoading && <div className="section-loading">Loading…</div>}
        {!sdOpenLoading && !sdOpenError && (
          <DataTable columns={OPEN_SD_COLUMNS} rows={serviceDisruptionRows} ariaLabel="Open Service Disruption" />
        )}
      </Panel>
      <Panel title="Open Support Bugs" accent="blue">
        {sbError && <div className="section-error">Couldn't load support bugs.</div>}
        {sbLoading && <div className="section-loading">Loading…</div>}
        {!sbLoading && !sbError && (
          <DataTable columns={OPEN_SUPPORT_COLUMNS} rows={supportBugsRows} ariaLabel="Open Support Bugs" />
        )}
      </Panel>
    </div>
  );
}

export default QualityOverviewTab;
